import { useEffect, useState } from 'react';
import { Modal } from '../../common/Modal';
import { Button } from '../../common/Button';
import {
  EventUpdateOptions,
  resolveEventUpdateOptions,
} from '../../../utils/eventUpdate';

export interface EventUpdateOptionsDialogProps {
  isOpen: boolean;
  /** Event name, shown so the admin can confirm they are editing the right one. */
  eventName: string;
  /**
   * How many riders have already RSVP'd. Shown verbatim in the warning: telling
   * an admin "this will clear 69 RSVP responses" is information, whereas
   * "this cannot be undone" on its own is decoration. (FR-1.9)
   */
  rsvpResponseCount: number;
  isSubmitting?: boolean;
  onConfirm: (options: EventUpdateOptions) => void;
  onCancel: () => void;
}

/**
 * Post-save options for editing a FUTURE event.
 *
 * Offers two choices that are independent in mechanism -- the reset is a flag on
 * the event update, the email is a separate endpoint -- but NOT independent in
 * effect. Choosing the reset forces the email, because a reset returns every
 * rider to "no response" and, on an event happening today, the daily reminder
 * has already run and will not run again. Without an email nothing would
 * re-prompt anybody and the expected-attendance count would sit at zero, which
 * is worse than the stale count the admin started with.
 *
 * The dialog only ever appears for events that have not started and are not
 * cancelled; that decision belongs to the caller
 * (`canPromptEventUpdateOptions`), not here.
 *
 * A single Modal is used deliberately: Modal hard-codes
 * `aria-labelledby="modal-title"`, so two simultaneously open dialogs would
 * collide on that id.
 */
export function EventUpdateOptionsDialog({
  isOpen,
  eventName,
  rsvpResponseCount,
  isSubmitting = false,
  onConfirm,
  onCancel,
}: EventUpdateOptionsDialogProps) {
  const [resetRsvps, setResetRsvps] = useState(false);
  const [sendEmailChecked, setSendEmailChecked] = useState(false);

  // Reset the checkboxes each time the dialog opens, so a previous decision is
  // never silently reapplied to a different save.
  useEffect(() => {
    if (isOpen) {
      setResetRsvps(false);
      setSendEmailChecked(false);
    }
  }, [isOpen]);

  // The email is forced on when a reset is selected. The checkbox below is
  // rendered checked AND disabled to match, rather than left enabled and then
  // silently overridden -- an enabled control that does not control anything
  // misrepresents what is about to happen. (FR-1.3)
  const emailForced = resetRsvps;
  const emailChecked = emailForced || sendEmailChecked;

  const handleConfirm = () => {
    onConfirm(resolveEventUpdateOptions(resetRsvps, sendEmailChecked));
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Update Event">
      <div className="space-y-4" data-testid="event-update-options-dialog">
        <p className="text-gray-700">
          Saving changes to <strong>{eventName}</strong>. Would you like to do
          either of the following?
        </p>

        {/* Option 1 - reset RSVPs */}
        <div className="border border-gray-200 rounded-md p-3">
          <label
            htmlFor="event-update-reset-rsvps"
            className="flex items-start gap-3 cursor-pointer"
          >
            <input
              id="event-update-reset-rsvps"
              data-testid="event-update-options-reset-checkbox"
              type="checkbox"
              className="rounded mt-1"
              checked={resetRsvps}
              disabled={isSubmitting}
              onChange={e => setResetRsvps(e.target.checked)}
            />
            <span className="text-sm text-gray-800">
              <span className="font-semibold block">
                Reset RSVP status for every rider
              </span>
              Riders can then RSVP again for the updated details, so the
              expected attendance count reflects the new time and place.
            </span>
          </label>

          {resetRsvps && (
            <p
              className="mt-2 ml-7 text-sm text-danger-600"
              data-testid="event-update-options-reset-warning"
            >
              {rsvpResponseCount === 1
                ? 'This will clear 1 RSVP response. '
                : `This will clear ${rsvpResponseCount} RSVP responses. `}
              This cannot be undone.
            </p>
          )}
        </div>

        {/* Option 2 - notification email */}
        <div className="border border-gray-200 rounded-md p-3">
          <label
            htmlFor="event-update-send-email"
            className={`flex items-start gap-3 ${
              emailForced ? 'cursor-default' : 'cursor-pointer'
            }`}
          >
            <input
              id="event-update-send-email"
              data-testid="event-update-options-email-checkbox"
              type="checkbox"
              className="rounded mt-1"
              checked={emailChecked}
              disabled={emailForced || isSubmitting}
              onChange={e => setSendEmailChecked(e.target.checked)}
            />
            {/* Kept at full contrast rather than greyed out: the text still has
                to be readable when the control is disabled. */}
            <span className="text-sm text-gray-800">
              <span className="font-semibold block">
                Send an update email to everyone
              </span>
              Includes the updated date, time and location, and asks riders to
              RSVP.
            </span>
          </label>

          {emailForced && (
            <p
              className="mt-2 ml-7 text-sm text-gray-600"
              data-testid="event-update-options-email-forced-note"
            >
              Included automatically because you are resetting RSVP status —
              otherwise nobody would know to respond again.
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-2 border-t">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="event-update-options-cancel-button"
          >
            Cancel
          </Button>
          <Button
            // `danger` when a reset is selected, because that button press
            // destroys data that cannot be recovered.
            variant={resetRsvps ? 'danger' : 'primary'}
            onClick={handleConfirm}
            disabled={isSubmitting}
            data-testid="event-update-options-confirm-button"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EventUpdateOptionsDialog;
